import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props { children: React.ReactNode }

/**
 * Gate for /apple-leasing.
 * - Must be signed in (email-verified by Supabase).
 * - Must be approved in pending_user_approvals OR have admin role.
 */
export function AppleProtectedRoute({ children }: Props) {
  const [state, setState] = useState<"loading" | "anon" | "pending" | "ok">("loading");
  const location = useLocation();

  useEffect(() => {
    let active = true;

    const check = async (sessionUserId?: string) => {
      try {
        let userId = sessionUserId;
        if (!userId) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) { active && setState("anon"); return; }
          userId = session.user.id;
        }
        const [{ data: role }, { data: approval }] = await Promise.all([
          supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle(),
          supabase.from("pending_user_approvals").select("status").eq("user_id", userId).maybeSingle(),
        ]);
        if (!active) return;
        if (role || approval?.status === "approved") setState("ok");
        else setState("pending");
      } catch {
        active && setState("anon");
      }
    };

    check();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) { setState("anon"); return; }
      setState("loading");
      setTimeout(() => check(session.user.id), 0);
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, []);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (state === "anon") {
    return <Navigate to="/apple-leasing/login" state={{ from: location }} replace />;
  }
  if (state === "pending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="max-w-md text-center p-6 border border-border rounded-lg bg-card">
          <h1 className="text-2xl font-bold mb-3">Access Pending Approval</h1>
          <p className="text-muted-foreground mb-4">
            Your account has been created and verified. A MuxSignal administrator will approve
            your access shortly. You'll receive an email when access is granted.
          </p>
          <Button onClick={() => supabase.auth.signOut()}>Sign Out</Button>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
